import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import IncidentTrendAnalysis from "@/components/IncidentTrendAnalysis";
import EnergyTrend from "@/components/EnergyTrend";
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Target
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#a855f7', '#ec4899', '#06b6d4', '#3b82f6', '#71717a'];

export default function Weekly() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/metrics/weekly`, { withCredentials: true });
      setMetrics(response.data);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      toast.error("Failed to load weekly metrics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const incidentsByType = Object.entries(metrics?.incidents?.by_type || {}).map(([name, value]) => ({
    name: name.replace(/([A-Z])/g, ' $1').trim(),
    value,
  }));

  const incidentsByUnit = Object.entries(metrics?.incidents?.by_unit || {}).map(([name, value]) => ({
    name,
    value,
  }));

  const totalIncidents = metrics?.incidents?.total || 0;
  const monthlyTotal = metrics?.monthly_comparison?.total || 0;
  const weeklyAverage = Math.round(monthlyTotal / 4);
  const trend = totalIncidents > weeklyAverage ? 'up' : totalIncidents < weeklyAverage ? 'down' : 'same';

  return (
    <div data-testid="weekly-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary" />
          Weekly Strategic Review
        </h1>
        <p className="text-muted-foreground">Your week as a director — productivity first, clinical ops below.</p>
      </div>

      {/* Productivity KPI Row — lead with what you control daily */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="weekly-productivity-row">
        <Card className="bento-card">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasks Completed</p>
                <p className="text-3xl font-bold mt-1">
                  {metrics?.productivity?.tasks_completed || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">this week</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Focus Blocks</p>
                <p className="text-3xl font-bold mt-1">
                  {metrics?.productivity?.focus_blocks_completed || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">completed</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Target className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Interruptions</p>
                <p className="text-3xl font-bold mt-1">
                  {metrics?.productivity?.total_interruptions || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">logged</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Energy & Output Trend (from checkouts) */}
      <EnergyTrend />

      {/* Clinical Ops section — secondary, behind a clear heading */}
      <div className="pt-4 border-t border-zinc-800/70">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-zinc-700" />
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold px-2">Clinical Ops Snapshot</span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-zinc-700" />
        </div>

        {/* Incidents KPI (one card, not the hero) */}
        <Card className="bento-card mb-4" data-testid="weekly-incident-kpi">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Incidents (7d)</p>
                <p className="text-3xl font-bold mt-1">{totalIncidents}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  vs {weeklyAverage} weekly avg ({trend === 'up' ? 'above' : trend === 'down' ? 'below' : 'at'})
                </p>
              </div>
              <div className={`p-2 rounded-lg ${
                trend === 'up' ? 'bg-red-500/20' :
                trend === 'down' ? 'bg-emerald-500/20' : 'bg-zinc-800'
              }`}>
                {trend === 'up' ? (
                  <TrendingUp className="w-5 h-5 text-red-400" />
                ) : trend === 'down' ? (
                  <TrendingDown className="w-5 h-5 text-emerald-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Incident Trend Analysis */}
        <IncidentTrendAnalysis />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Incidents by Type */}
        <Card className="bento-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Incidents by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {incidentsByType.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={incidentsByType}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {incidentsByType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181b', 
                        border: '1px solid #27272a',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No incident data for this period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Incidents by Unit */}
        <Card className="bento-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Incidents by Unit</CardTitle>
          </CardHeader>
          <CardContent>
            {incidentsByUnit.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={incidentsByUnit} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis type="number" stroke="#71717a" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#71717a"
                      width={80}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181b', 
                        border: '1px solid #27272a',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No incident data for this period
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Comparison */}
      <Card className="bento-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">30-Day Type Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(metrics?.monthly_comparison?.by_type || {}).map(([type, count]) => (
              <div 
                key={type}
                className="p-3 rounded-lg bg-zinc-800/50 text-center"
              >
                <p className="text-xs text-muted-foreground">
                  {type.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                <p className="text-2xl font-bold mt-1">{count}</p>
              </div>
            ))}
            {Object.keys(metrics?.monthly_comparison?.by_type || {}).length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full text-center py-4">
                No incident data for the past 30 days
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Print-friendly summary */}
      <Card className="bento-card print:shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Weekly Summary</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm prose-invert max-w-none">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-muted-foreground mb-2">Incidents</h4>
              <ul className="space-y-1">
                <li>Total this week: {totalIncidents}</li>
                <li>Trend: {trend === 'up' ? '↑ Above' : trend === 'down' ? '↓ Below' : '→ At'} average</li>
                <li>Monthly total: {monthlyTotal}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-muted-foreground mb-2">Productivity</h4>
              <ul className="space-y-1">
                <li>Tasks completed: {metrics?.productivity?.tasks_completed || 0}</li>
                <li>Focus blocks: {metrics?.productivity?.focus_blocks_completed || 0}</li>
                <li>Interruptions: {metrics?.productivity?.total_interruptions || 0}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-muted-foreground mb-2">Focus Areas</h4>
              <ul className="space-y-1">
                {incidentsByUnit.slice(0, 3).map(u => (
                  <li key={u.name}>{u.name}: {u.value} incidents</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
